import * as QueryContext from './generated_context'
import * as Model from './generated_models'
import { AsyncState, loadingAsyncState, HttpResult } from 'widgets-for-react'
import { Locale as SiteLocale } from './components/drupal/types_and_parser/extra_types.mod'
import { List } from 'immutable'
import { FilterBuilderComplex } from 'typescript-odata-client'

export const odataUri = '/api/v1/odata/GetQuery?query='

type DBLocale = Pick<Model.Locale, 'region' | 'language'>

const globalDBLocale: DBLocale = { region: 'global', language: 'en' }

const DBLocales: Map<SiteLocale, DBLocale> = new Map([
  ['global', globalDBLocale],
  ['ae', { region: 'ae', language: 'ar' }],
  ['au', { region: 'au', language: 'en' }],
  ['be_nl', { region: 'be', language: 'nl' }],
  ['be_fr', { region: 'be', language: 'fr' }],
  ['cn', { region: 'cn', language: 'zh' }],
  ['de', { region: 'de', language: 'de' }],
  ['dk', { region: 'dk', language: 'da' }],
  ['es', { region: 'es', language: 'es' }],
  ['fr', { region: 'fr', language: 'fr' }],
  ['hk', { region: 'hk', language: 'zh' }],
  ['ie', { region: 'ie', language: 'en' }],
  ['in', { region: 'in', language: 'en' }],
  ['it', { region: 'it', language: 'it' }],
  ['fi', { region: 'fi', language: 'fi' }],
  ['mx', { region: 'mx', language: 'es' }],
  ['my', { region: 'my', language: 'zh' }],
  ['nl', { region: 'nl', language: 'nl' }],
  ['no', { region: 'no', language: 'no' }],
  ['ru', { region: 'ru', language: 'ru' }],
  ['se', { region: 'se', language: 'sv' }],
  ['na', { region: 'na', language: 'en' }],
  ['uk', { region: 'uk', language: 'en' }],
  ['na-fr', { region: 'na', language: 'fr' }],
  ['na-es', { region: 'na', language: 'es' }],
  ['us', { region: 'us', language: 'en' }],
  ['pl', { region: 'pl', language: 'pl' }]
])

export const loadProducts = async (
  document: 'bim' | 'techdrawing' | 'both',
  country: SiteLocale
): Promise<HttpResult<List<string>>> => {
  const filter = (query: FilterBuilderComplex<QueryContext.Product>) =>
    document == 'bim'
      ? query.hasBIM.Equals(true)
      : document == 'techdrawing'
      ? query.hasTechnicalDrawing.Equals(true)
      : query.hasBIM.Equals(true).And(query.hasTechnicalDrawing.Equals(true))

  const DBlocale = DBLocales.get(country)

  const globalProductsQuery = QueryContext.GlobalProductQuery(odataUri).Select('name').Filter(filter)

  const subsidiaryProductsQuery = QueryContext.LocaleQuery(odataUri)
    .Select('region', 'language')
    .Filter((locale) => locale.language.Equals(DBlocale.language).And(locale.region.Equals(DBlocale.region)))
    .Expand('Subsidiary_Locale', (query) =>
      query
        .Select('LocaleId', 'SubsidiaryId')
        .Expand('Subsidiary', (query) =>
          query
            .Select('Id')
            .Expand('Subsidiary_SubsidiaryProduct', (query) =>
              query
                .Select('SubsidiaryId', 'SubsidiaryProductId')
                .Expand('SubsidiaryProduct', (query) => query.Select('name').Filter(filter))
            )
        )
    )

  const globalProducts = (await globalProductsQuery.ToList()).map((x) => x.name).toList()

  const subsidiaryProducts = async () =>
    (await subsidiaryProductsQuery.ToList())
      .flatMap((x) =>
        x.Subsidiary_Locale.flatMap((y) =>
          y.Subsidiary.flatMap((z) =>
            z.Subsidiary_SubsidiaryProduct.flatMap((x) => x.SubsidiaryProduct.map((x) => x.name))
          )
        )
      )
      .toList()

  const products = country == 'global' ? globalProducts : (await subsidiaryProducts()).concat(globalProducts).toList()

  return {
    kind: 'result',
    status: 200,
    value: products
  }
}

const getSubsidiaryProductQuery = (name: string) => {
  return (
    QueryContext.SubsidiaryProductQuery(odataUri)
      .Select('Id', 'name', 'kind', 'image', 'hasBIM', 'hasTechnicalDrawing')
      // Default PRoperties [Optional]
      .Expand('SubsidiaryProduct_Property', (gp_pt) =>
        gp_pt
          .Select('Id', 'SubsidiaryProductId', 'PropertyId')
          .ExpandStrict('Property', (pt) => pt.Select('Id', 'name', 'kind', 'value'))
      )
      // Unlocalized Header [Required]
      .Expand('SubsidiaryProduct_UnLocalizedProductHeader', (gp_ulph) =>
        gp_ulph
          .Select('Id', 'SubsidiaryProductId', 'UnLocalizedProductHeaderId')
          .ExpandStrict('UnLocalizedProductHeader', (ulph) =>
            ulph
              .Select('Id', 'pageTitle', 'descriptionTitle', 'description')
              .Expand('UnLocalizedProductHeader_Usp', (ulph_usp) =>
                ulph_usp
                  .Select('Id', 'UnLocalizedProductHeaderId', 'UspId')
                  .ExpandStrict('Usp', (usp) => usp.Select('Id', 'value'))
              )
          )
      )
      // Localized Header [Optional]
      .Expand('SubsidiaryProduct_LocalizedProductHeader', (gp_lph) =>
        gp_lph
          .Select('Id', 'SubsidiaryProductId', 'LocalizedProductHeaderId')
          .ExpandStrict('LocalizedProductHeader', (lph) =>
            lph
              .Select('Id', 'pageTitle', 'descriptionTitle', 'description')
              .Expand('LocalizedProductHeader_Usp', (lph_usp) =>
                lph_usp
                  .Select('Id', 'LocalizedProductHeaderId', 'UspId')
                  .ExpandStrict('Usp', (usp) => usp.Select('Id', 'value'))
              )
              .ExpandStrict('LocalizedProductHeader_Locale', (lph_l) =>
                lph_l
                  .Select('Id', 'LocalizedProductHeaderId', 'LocaleId')
                  .ExpandStrict('Locale', (l) => l.Select('Id', 'region', 'language'))
              )
          )
      )
      // Localized Property Group [Optional]
      .Expand('SubsidiaryProduct_LocalizedPropertyGroup', (gp_lph) =>
        gp_lph
          .Select('Id', 'SubsidiaryProductId', 'LocalizedPropertyGroupId')
          .ExpandStrict('LocalizedPropertyGroup', (lpg) =>
            lpg
              .Select('Id')
              .Expand('LocalizedPropertyGroup_Property', (lpg_pt) =>
                lpg_pt
                  .Select('Id', 'LocalizedPropertyGroupId', 'PropertyId')
                  .ExpandStrict('Property', (pt) => pt.Select('Id', 'kind', 'name', 'value'))
              )
              .ExpandStrict('LocalizedPropertyGroup_Locale', (lpg_l) =>
                lpg_l
                  .Select('Id', 'LocalizedPropertyGroupId', 'LocaleId')
                  .ExpandStrict('Locale', (l) => l.Select('Id', 'region', 'language'))
              )
          )
      )
      .Where((x) => x.name.EqualsCaseInsensitive(name.replace(/-/g, ' ')))
      .First()
  )
}

const getGlobalProductQuery = (name: string) => {
  return (
    QueryContext.GlobalProductQuery(odataUri)
      .Select('Id', 'name', 'kind', 'image', 'hasBIM', 'hasTechnicalDrawing')
      // Default PRoperties [Optional]
      .Expand('GlobalProduct_Property', (gp_pt) =>
        gp_pt
          .Select('Id', 'GlobalProductId', 'PropertyId')
          .ExpandStrict('Property', (pt) => pt.Select('Id', 'name', 'kind', 'value'))
      )
      // Unlocalized Header [Required]
      .Expand('GlobalProduct_UnLocalizedProductHeader', (gp_ulph) =>
        gp_ulph
          .Select('Id', 'GlobalProductId', 'UnLocalizedProductHeaderId')
          .ExpandStrict('UnLocalizedProductHeader', (ulph) =>
            ulph
              .Select('Id', 'pageTitle', 'descriptionTitle', 'description')
              .Expand('UnLocalizedProductHeader_Usp', (ulph_usp) =>
                ulph_usp
                  .Select('Id', 'UnLocalizedProductHeaderId', 'UspId')
                  .ExpandStrict('Usp', (usp) => usp.Select('Id', 'value'))
              )
          )
      )
      // Localized Header [Optional]
      .Expand('GlobalProduct_LocalizedProductHeader', (gp_lph) =>
        gp_lph
          .Select('Id', 'GlobalProductId', 'LocalizedProductHeaderId')
          .ExpandStrict('LocalizedProductHeader', (lph) =>
            lph
              .Select('Id', 'pageTitle', 'descriptionTitle', 'description')
              .Expand('LocalizedProductHeader_Usp', (lph_usp) =>
                lph_usp
                  .Select('Id', 'LocalizedProductHeaderId', 'UspId')
                  .ExpandStrict('Usp', (usp) => usp.Select('Id', 'value'))
              )
              .ExpandStrict('LocalizedProductHeader_Locale', (lph_l) =>
                lph_l
                  .Select('Id', 'LocalizedProductHeaderId', 'LocaleId')
                  .ExpandStrict('Locale', (l) => l.Select('Id', 'region', 'language'))
              )
          )
      )
      // Localized Property Group [Optional]
      .Expand('GlobalProduct_LocalizedPropertyGroup', (gp_lph) =>
        gp_lph
          .Select('Id', 'GlobalProductId', 'LocalizedPropertyGroupId')
          .ExpandStrict('LocalizedPropertyGroup', (lpg) =>
            lpg
              .Select('Id')
              .Expand('LocalizedPropertyGroup_Property', (lpg_pt) =>
                lpg_pt
                  .Select('Id', 'LocalizedPropertyGroupId', 'PropertyId')
                  .ExpandStrict('Property', (pt) => pt.Select('Id', 'kind', 'name', 'value'))
              )
              .ExpandStrict('LocalizedPropertyGroup_Locale', (lpg_l) =>
                lpg_l
                  .Select('Id', 'LocalizedPropertyGroupId', 'LocaleId')
                  .ExpandStrict('Locale', (l) => l.Select('Id', 'region', 'language'))
              )
          )
      )
      .Where((x) => x.name.EqualsCaseInsensitive(name.replace(/-/g, ' ')))
      .First()
  )
}

type FromModelId<T> = Omit<T, 'CreatedDate' | 'Kind'>
type FromModel<T> = Omit<T, 'Id' | 'CreatedDate' | 'Kind'>

export type ProductProperty = FromModel<Model.Property>
export type ProductDetails = FromModelId<Model.Product> & {
  properties: FromModel<Model.Property>[]
  unLocalizedProductHeader: FromModel<Model.UnLocalizedProductHeader> & {
    usps: FromModel<Model.Usp>[]
  }
  localizedProductHeader: FromModel<Model.LocalizedProductHeader> & {
    usps: FromModel<Model.Usp>[]
    locale: FromModel<Model.Locale>
  }
  localizedPropertyGroups: (FromModel<Model.LocalizedPropertyGroup> & {
    properties: FromModel<Model.Property>[]
    locales: FromModel<Model.Locale>[]
  })[]
}

const getSubsidiaryProduct = (name: string, siteLocale: SiteLocale): Promise<HttpResult<ProductDetails>> =>
  getSubsidiaryProductQuery(name).then((p) => {
    const dbLocale = DBLocales.get(siteLocale) || globalDBLocale
    const productDetails: ProductDetails = {
      Id: p.Id,
      kind: p.kind as Model.Product['kind'],
      name: p.name,
      image: p.image,
      hasBIM: p.hasBIM,
      hasTechnicalDrawing: p.hasTechnicalDrawing,
      properties: p.SubsidiaryProduct_Property.flatMap((gp_pt) =>
        gp_pt.Property.map((pt) => ({
          name: pt.name,
          value: pt.value,
          kind: pt.kind as Model.Property['kind']
        }))
      ).toArray(),
      unLocalizedProductHeader: p.SubsidiaryProduct_UnLocalizedProductHeader.flatMap((gp_ulph) =>
        gp_ulph.UnLocalizedProductHeader.map((ulph) => ({
          pageTitle: ulph.pageTitle,
          descriptionTitle: ulph.descriptionTitle,
          description: ulph.description,
          usps: ulph.UnLocalizedProductHeader_Usp.flatMap((ulph_usp) =>
            ulph_usp.Usp.map((usp) => ({
              value: usp.value
            }))
          ).toArray()
        }))
      ).first(),
      localizedProductHeader: p.SubsidiaryProduct_LocalizedProductHeader.flatMap((gp_lph) =>
        gp_lph.LocalizedProductHeader.map((lph) => ({
          pageTitle: lph.pageTitle,
          descriptionTitle: lph.descriptionTitle,
          description: lph.description,
          usps: lph.LocalizedProductHeader_Usp.flatMap((lph_usp) =>
            lph_usp.Usp.map((usp) => ({
              value: usp.value
            }))
          ).toArray(),
          locale: lph.LocalizedProductHeader_Locale.flatMap((lph_l) =>
            lph_l.Locale.map((l) => ({
              region: (l.region || 'global') as Model.Locale['region'],
              language: (l.language || 'en') as Model.Locale['language']
            }))
          ).first()
        }))
      )
        .filter((lph) => lph.locale.region == dbLocale.region && lph.locale.language == dbLocale.language)
        .first(),
      localizedPropertyGroups: p.SubsidiaryProduct_LocalizedPropertyGroup.flatMap((gp_lpg) =>
        gp_lpg.LocalizedPropertyGroup.map((lpg) => ({
          properties: lpg.LocalizedPropertyGroup_Property.flatMap((lpg_pt) =>
            lpg_pt.Property.map((pt) => ({
              name: pt.name,
              value: pt.value,
              kind: pt.kind as Model.Property['kind']
            }))
          ).toArray(),
          locales: lpg.LocalizedPropertyGroup_Locale.flatMap((lpg_l) =>
            lpg_l.Locale.map((l) => ({
              region: (l.region || 'global') as Model.Locale['region'],
              language: (l.language || 'en') as Model.Locale['language']
            }))
          ).toArray()
        }))
      )
        .filter((lpg) => lpg.locales.some((l) => l.region == dbLocale.region && l.language == dbLocale.language))
        .toArray()
    }
    const res: HttpResult<ProductDetails> = {
      kind: 'result',
      status: 200,
      value: productDetails
    }
    return res
  })

const getGlobalProduct = (name: string, siteLocale: SiteLocale): Promise<HttpResult<ProductDetails>> =>
  getGlobalProductQuery(name).then((p) => {
    const dbLocale = DBLocales.get(siteLocale) || globalDBLocale
    const productDetails: ProductDetails = {
      Id: p.Id,
      kind: p.kind as Model.Product['kind'],
      name: p.name,
      image: p.image,
      hasBIM: p.hasBIM,
      hasTechnicalDrawing: p.hasTechnicalDrawing,
      properties: p.GlobalProduct_Property.flatMap((gp_pt) =>
        gp_pt.Property.map((pt) => ({
          name: pt.name,
          value: pt.value,
          kind: pt.kind as Model.Property['kind']
        }))
      ).toArray(),
      unLocalizedProductHeader: p.GlobalProduct_UnLocalizedProductHeader.flatMap((gp_ulph) =>
        gp_ulph.UnLocalizedProductHeader.map((ulph) => ({
          pageTitle: ulph.pageTitle,
          descriptionTitle: ulph.descriptionTitle,
          description: ulph.description,
          usps: ulph.UnLocalizedProductHeader_Usp.flatMap((ulph_usp) =>
            ulph_usp.Usp.map((usp) => ({
              value: usp.value
            }))
          ).toArray()
        }))
      ).first(),
      localizedProductHeader: p.GlobalProduct_LocalizedProductHeader.flatMap((gp_lph) =>
        gp_lph.LocalizedProductHeader.map((lph) => ({
          pageTitle: lph.pageTitle,
          descriptionTitle: lph.descriptionTitle,
          description: lph.description,
          usps: lph.LocalizedProductHeader_Usp.flatMap((lph_usp) =>
            lph_usp.Usp.map((usp) => ({
              value: usp.value
            }))
          ).toArray(),
          locale: lph.LocalizedProductHeader_Locale.flatMap((lph_l) =>
            lph_l.Locale.map((l) => ({
              region: (l.region || 'global') as Model.Locale['region'],
              language: (l.language || 'en') as Model.Locale['language']
            }))
          ).first()
        }))
      )
        .filter((lph) => lph.locale.region == dbLocale.region && lph.locale.language == dbLocale.language)
        .first(),
      localizedPropertyGroups: p.GlobalProduct_LocalizedPropertyGroup.flatMap((gp_lpg) =>
        gp_lpg.LocalizedPropertyGroup.map((lpg) => ({
          properties: lpg.LocalizedPropertyGroup_Property.flatMap((lpg_pt) =>
            lpg_pt.Property.map((pt) => ({
              name: pt.name,
              value: pt.value,
              kind: pt.kind as Model.Property['kind']
            }))
          ).toArray(),
          locales: lpg.LocalizedPropertyGroup_Locale.flatMap((lpg_l) =>
            lpg_l.Locale.map((l) => ({
              region: (l.region || 'global') as Model.Locale['region'],
              language: (l.language || 'en') as Model.Locale['language']
            }))
          ).toArray()
        }))
      )
        .filter((lpg) => lpg.locales.some((l) => l.region == dbLocale.region && l.language == dbLocale.language))
        .toArray()
    }
    const res: HttpResult<ProductDetails> = {
      kind: 'result',
      status: 200,
      value: productDetails
    }
    return res
  })

export const getProduct = (name: string, siteLocale: SiteLocale): AsyncState<ProductDetails> =>
  loadingAsyncState(() => getGlobalProduct(name, siteLocale).catch((_) => getSubsidiaryProduct(name, siteLocale)))
